// Shift Service — Daily shift overrides for barbers
// Shifts override permanent availability for a specific date.
// Use case: "Kelly came in late today", "Ryan took lunch 1-2pm", "Jose left at 5"

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../config/firebase';

export interface Shift {
  id: string; // barberId_YYYY-MM-DD
  barberId: string;
  date: string; // YYYY-MM-DD
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  isOff: boolean; // true = barber is completely off today (overrides availability)
  note?: string; // "Came in late", "Doctor appointment", etc.
  updatedAt: Date;
  updatedBy?: string; // manager who made the change
}

class ShiftService {
  private shifts: Map<string, Shift> = new Map();
  private unsubscribe: (() => void) | null = null;
  private listeners: Set<() => void> = new Set();

  constructor() {
    this.startListening();
    // ROOT CAUSE of "shift saves but never shows on the calendar" (Jose 2026-10-29): this singleton builds at
    // MODULE LOAD — usually BEFORE Firebase Auth has a signed-in user. A `shifts` read with no auth is
    // permission-denied, and onSnapshot used to fail SILENTLY (no error cb, no retry) → the shifts Map stayed
    // empty forever → getShift() always returned undefined → the grid overlay + availability engine fell back
    // to the barber's WEEKLY hours and the edited shift never painted. Re-subscribe the instant auth is ready
    // so the read runs WITH a signed-in staff user and the shifts actually arrive + repaint.
    try {
      onAuthStateChanged(auth, (user) => { if (user) this.startListening(); });
    } catch (e) { console.warn('[shiftService] auth listener attach failed:', e); }
  }

  private startListening() {
    try { this.unsubscribe?.(); } catch { /* ignore */ }
    this.unsubscribe = null;
    const colRef = collection(db, 'shifts');
    // One-shot authoritative load (Jose 2026-11-04): don't depend solely on the realtime listener's first
    // snapshot — if it's slow/cold in a session, getShift() returns undefined and the calendar + booking fall
    // back to WEEKLY hours (the edited shift silently does nothing). This guarantees the Map warms once auth
    // is ready. Best-effort; the onSnapshot below keeps it live after.
    getDocs(colRef).then((snap) => {
      snap.forEach((d) => {
        const x = d.data();
        this.shifts.set(d.id, {
          id: d.id, barberId: x.barberId, date: x.date,
          startTime: x.startTime || '09:00', endTime: x.endTime || '18:00',
          isOff: x.isOff || false, note: x.note || '',
          updatedAt: x.updatedAt?.toDate?.() || new Date(), updatedBy: x.updatedBy || '',
        });
      });
      if (snap.size) this.notifyListeners();
    }).catch(() => { /* permission/transport — the onSnapshot + auth re-subscribe cover it */ });
    this.unsubscribe = onSnapshot(
      colRef,
      (snapshot) => {
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            this.shifts.delete(change.doc.id);
          } else {
            const data = change.doc.data();
            this.shifts.set(change.doc.id, {
              id: change.doc.id,
              barberId: data.barberId,
              date: data.date,
              startTime: data.startTime || '09:00',
              endTime: data.endTime || '18:00',
              isOff: data.isOff || false,
              note: data.note || '',
              updatedAt: data.updatedAt?.toDate?.() || new Date(),
              updatedBy: data.updatedBy || '',
            });
          }
        });
        this.notifyListeners();
      },
      (err) => {
        // Don't die silently — log it; the auth listener above re-subscribes on the next sign-in.
        console.warn('[shiftService] shifts snapshot error (will retry on auth change):', (err as any)?.message || err);
      }
    );
  }

  private notifyListeners() {
    this.listeners.forEach((fn) => fn());
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // Deterministic doc ID: barberId_YYYY-MM-DD
  private makeId(barberId: string, date: string): string {
    return `${barberId}_${date}`;
  }

  // Get shift override for a barber on a specific date
  getShift(barberId: string, date: string): Shift | undefined {
    return this.shifts.get(this.makeId(barberId, date));
  }

  // Save or update a shift override
  async saveShift(barberId: string, date: string, data: {
    startTime: string;
    endTime: string;
    isOff: boolean;
    note?: string;
    updatedBy?: string;
  }): Promise<void> {
    const id = this.makeId(barberId, date);
    const docRef = doc(db, 'shifts', id);
    await setDoc(docRef, {
      barberId,
      date,
      startTime: data.startTime,
      endTime: data.endTime,
      isOff: data.isOff,
      note: data.note || '',
      updatedBy: data.updatedBy || '',
      updatedAt: Timestamp.now(),
    });
    // REPAINT IMMEDIATELY (Jose 2026-11-04 — ROOT of "Edit Shift shows success but the calendar/booking never
    // change"): the write above succeeded, but this service relied on the realtime `shifts` listener to echo it
    // back and repaint. When that listener is cold/slow in a session, getShift() stayed empty → the calendar
    // overlay AND the booking gate (both read this Map) fell back to WEEKLY hours → the edit did nothing. So
    // update the Map + notify NOW; the snapshot later confirms/merges the same value.
    this.shifts.set(id, {
      id, barberId, date,
      startTime: data.startTime, endTime: data.endTime,
      isOff: data.isOff, note: data.note || '',
      updatedAt: new Date(), updatedBy: data.updatedBy || '',
    });
    this.notifyListeners();
  }

  // Remove a shift override (reverts to permanent availability)
  async removeShift(barberId: string, date: string): Promise<void> {
    const id = this.makeId(barberId, date);
    const docRef = doc(db, 'shifts', id);
    await deleteDoc(docRef);
    // Reflect the revert immediately (same reason as saveShift) so the calendar returns to weekly hours now.
    this.shifts.delete(id);
    this.notifyListeners();
  }

  // Get all shifts for a barber (useful for week view)
  getShiftsForBarber(barberId: string): Shift[] {
    return Array.from(this.shifts.values()).filter(s => s.barberId === barberId);
  }

  // Get all shifts for a specific date (useful for day view)
  getShiftsForDate(date: string): Shift[] {
    return Array.from(this.shifts.values()).filter(s => s.date === date);
  }
}

export const shiftService = new ShiftService();
